import {
    CSSProperties,
    FC,
    KeyboardEvent,
    ReactNode,
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Popover, Portal, usePopoverContext } from '@mantine/core';
import { clsx } from 'clsx';
import List from '../../atoms/Menu/List';
import { MenuContext, type IMenuContextValue } from '../../atoms/Menu/MenuContext';
import { FieldA11yContext } from '../../context/FieldA11yContext';
import { useLatest } from '../../hooks/useLatest';
import { useMenuKeyboard } from '../../hooks/useMenuKeyboard';
import { pushModalLayer } from '../../lib/modalLayers';
import { POPOVER_Z_INDEX } from '../../lib/zIndex';
import { usePortalTarget } from '../../provider/UiContext';
import type { IListElement } from '../../types/IListElement';
import type { ContextMenuOpenedBy, IContextMenuPoint } from './contextMenuTrigger';
// вид пунктов и поверхности — меню пакета (AnchoredDialog + List)
import '../../atoms/Menu/List/List.scss';
import '../AnchoredDialog/AnchoredDialog.scss';
import './ContextMenu.scss';

/** Сколько ждать анимацию закрытия, прежде чем убрать портал, мс */
const EXIT_MS = 200;

export interface IContextMenuProps {
    opened: boolean;
    /** Точка открытия в координатах окна; после закрытия — прежняя (меню уходит из неё) */
    point: IContextMenuPoint | null;
    /** Закрыть: пункт выполнен, Escape, Tab, щелчок мимо, прокрутка колесом, смена размера окна */
    onClose: () => void;
    /** Пункты — меню пакета (`List`): вид, клавиатура, вложенные меню (`submenu`, `submenuPanel`) */
    items?: IListElement[];
    /** Свои пункты вместо `items` (`role="menuitem"`, кнопки): клавиатура — `menuNavigation`, вид — свой (`className`) */
    children?: ReactNode;
    /** Имя меню для диктора: что под ним @default 'Контекстное меню' */
    label?: string;
    /** Над пунктами: что под меню (узел, выделение) */
    header?: ReactNode;
    /**
     * Чем открыто: с клавиатуры (Shift+F10, клавиша меню) фокус сразу на первом пункте, указателем — на самом меню
     * (стрелки ведут к пунктам) @default pointer
     */
    openedBy?: ContextMenuOpenedBy;
    /**
     * Куда вернуть фокус после закрытия (кроме щелчка мимо — фокус ставит сам щелчок). Без значения — элемент, где он
     * был при открытии; `null` — никуда.
     */
    returnFocus?: HTMLElement | null;
    /** Граница, в которой меню переворачивается и сдвигается (холст); по умолчанию — окно */
    boundary?: Element | null;
    /** Класс панели: к виду меню пакета (`items`) или свой вид целиком (`children`) */
    className?: string;
    style?: CSSProperties;
    zIndex?: number;
    /** Тест-якорь панели; у списка пунктов (`role="menu"`) — `{data-testid}-menu` @default 'rf-context-menu' */
    'data-testid'?: string;
    /** data-атрибуты панели: вид цели и т. п. */
    [attribute: `data-${string}`]: string | undefined;
}

/**
 * Якорь — точка курсора виртуальным элементом floating-ui, а не узлом с `position: fixed`: `transform` предка
 * (Mantine оставляет `scale(1)` на контенте модалки после анимации, хост порталов области бывает внутри окна) делает
 * его containing block для fixed, и меню уезжало на смещение угла этого предка.
 */
const CursorAnchor: FC<{ point: IContextMenuPoint }> = ({ point: { x, y } }) => {
    const { reference } = usePopoverContext();
    useLayoutEffect(() => {
        const rect = { x, y, left: x, top: y, right: x, bottom: y, width: 0, height: 0 };
        // контекст Mantine типизирован под узел, а floating-ui принимает и виртуальный элемент
        reference({ getBoundingClientRect: () => ({ ...rect, toJSON: () => rect }) } as unknown as HTMLElement);
    }, [reference, x, y]);
    return null;
};

/** Элемент в фокусе до открытия: эффект стоит перед порталом меню и срабатывает раньше, чем меню заберёт фокус */
const FocusBeforeOpen: FC<{ opened: boolean; onCapture: (element: HTMLElement | null) => void }> = ({
    opened,
    onCapture,
}) => {
    const capture = useLatest(onCapture);
    useLayoutEffect(() => {
        if (!opened) return;
        const active = document.activeElement;
        capture.current(active instanceof HTMLElement && active !== document.body ? active : null);
    }, [opened, capture]);
    return null;
};

/** Свои пункты: роль меню и клавиатура меню пакета */
const CustomMenu: FC<{ id: string; labelledBy: string; testId: string; open: boolean; children: ReactNode }> = ({
    id,
    labelledBy,
    testId,
    open,
    children,
}) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const onKeyDown = useMenuKeyboard(menuRef, open);
    return (
        <div
            ref={menuRef}
            id={id}
            role="menu"
            aria-labelledby={labelledBy}
            tabIndex={-1}
            className="rf-context-menu__menu"
            data-testid={testId}
            onKeyDown={onKeyDown}
        >
            {children}
        </div>
    );
};

/**
 * Контекстное меню в точке вызова: портал в хост `usePortalTarget()`, пункты — меню пакета (стрелки, Home/End, буква,
 * сочетания, вложенные меню) или свои с той же клавиатурой. Escape (стек окон `modalLayers`), Tab, выбор пункта
 * закрывают меню и возвращают фокус туда, где он был; щелчок мимо, колесо и смена размера окна — закрывают.
 * Открывать удобно хуком `useContextMenu`.
 */
const ContextMenu: FC<IContextMenuProps> = ({
    opened: openedProp,
    point,
    onClose,
    items,
    children,
    label = 'Контекстное меню',
    header,
    openedBy = 'pointer',
    returnFocus,
    boundary,
    className,
    style,
    zIndex = POPOVER_Z_INDEX,
    'data-testid': testId = 'rf-context-menu',
    ...data
}) => {
    const uid = useId();
    const labelId = `${uid}-label`;
    const menuId = `${uid}-menu`;
    const panelId = `${uid}-panel`;
    const portalTarget = usePortalTarget();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const opened = openedProp && point !== null;
    const close = useLatest(onClose);

    // точка и портал держатся до конца анимации закрытия
    const [anchor, setAnchor] = useState(point);
    if (point && (!Object.is(point.x, anchor?.x) || !Object.is(point.y, anchor?.y))) setAnchor(point);
    const [present, setPresent] = useState(opened);
    if (opened && !present) setPresent(true);
    useEffect(() => {
        if (opened || !present) return;
        const timer = window.setTimeout(() => setPresent(false), EXIT_MS);
        return () => window.clearTimeout(timer);
    }, [opened, present]);

    const captured = useRef<HTMLElement | null>(null);
    const returnTo = useLatest(returnFocus);
    // куда вернуть фокус — решается при открытии: после закрытия владелец может уже сбросить returnFocus
    const openedFrom = useRef<HTMLElement | null>(null);
    const closedByOutsideClick = useRef(false);
    const latestOpened = useLatest(opened);
    // меню, которое владелец размонтировал при закрытии, уже не откроется снова
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const reopened = useCallback(() => mounted.current && latestOpened.current, [latestOpened]);
    const outsideClick = useCallback(() => closedByOutsideClick.current, []);

    // Фокус был в меню (пункт, Escape, прокрутка) или пропал — туда, где он был до открытия. Ушёл на другой элемент
    // (пункт открыл окно, поле) — не трогаем. Позже, а не сразу: из keydown Enter перенесённый фокус получил бы и
    // keypress/keyup — кнопка-цель открыла бы меню снова.
    useEffect(() => {
        if (!opened) return;
        closedByOutsideClick.current = false;
        const target = returnTo.current === undefined ? captured.current : returnTo.current;
        openedFrom.current = target;
        return () => {
            const panel = document.getElementById(panelId);
            window.setTimeout(() => {
                const active = document.activeElement;
                const lost = !active || active === document.body || !!panel?.contains(active);
                if (lost && !reopened() && !outsideClick() && target?.isConnected) {
                    target.focus({ preventScroll: true });
                }
            });
        };
    }, [opened, panelId, returnTo, reopened, outsideClick]);

    // Escape — через стек окон: меню в окне закрывается одно, без окна под ним
    useEffect(() => {
        if (!opened) return;
        return pushModalLayer({
            root: () => dropdownRef.current,
            dialog: () => dropdownRef.current,
            anchored: true,
            onEscape: () => {
                close.current();
                return true;
            },
        });
    }, [opened, close]);

    // Колесо и касание мимо меню прокручивают то, к чему оно относится, — меню закрывается
    useEffect(() => {
        if (!opened) return;
        const outside = (event: Event) =>
            !(event.target instanceof Node && dropdownRef.current?.contains(event.target));
        const onScrollIntent = (event: Event) => {
            if (outside(event)) close.current();
        };
        const onResize = () => close.current();
        window.addEventListener('wheel', onScrollIntent, { capture: true, passive: true });
        window.addEventListener('touchmove', onScrollIntent, { capture: true, passive: true });
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('wheel', onScrollIntent, { capture: true });
            window.removeEventListener('touchmove', onScrollIntent, { capture: true });
            window.removeEventListener('resize', onResize);
        };
    }, [opened, close]);

    const menuContext = useMemo<IMenuContextValue>(
        () => ({
            onClose,
            show: opened,
            popupRole: 'menu',
            menuId,
            labelledBy: labelId,
            openFrom: openedBy === 'keyboard' ? 'first' : 'menu',
        }),
        [onClose, opened, menuId, labelId, openedBy],
    );

    /** Tab уводит из меню: фокус — туда, где был, дальше браузер переходит к следующему за ним элементу */
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Tab') return;
        const target = openedFrom.current;
        close.current();
        if (target?.isConnected) target.focus({ preventScroll: true });
        else event.preventDefault();
    };

    const middlewareArea = boundary ? { boundary, padding: 8 } : undefined;

    return (
        <>
            {returnFocus === undefined && (
                <FocusBeforeOpen opened={opened} onCapture={(element) => (captured.current = element)} />
            )}
            {/* Свой портал, а не портал дропдауна: в тестовом окружении Mantine дропдаун рисуется на месте */}
            {present && anchor && (
                <Portal target={portalTarget}>
                    <Popover
                        opened={opened}
                        onChange={(next) => {
                            if (next) return;
                            closedByOutsideClick.current = true;
                            close.current();
                        }}
                        position="bottom-start"
                        offset={2}
                        zIndex={zIndex}
                        withinPortal={false}
                        withRoles={false}
                        closeOnEscape={false}
                        trapFocus={false}
                        returnFocus={false}
                        hideDetached={false}
                        middlewares={{ flip: middlewareArea ?? true, shift: middlewareArea ?? true }}
                    >
                        <CursorAnchor point={anchor} />
                        <Popover.Dropdown
                            ref={dropdownRef}
                            id={panelId}
                            className={clsx(
                                'rf-context-menu',
                                items && [
                                    'rf-anchored-dialog',
                                    'rf-anchored-dialog--menu',
                                    'rf-anchored-dialog--content-width',
                                    'rf-anchored-list-dialog',
                                ],
                                className,
                            )}
                            style={style}
                            onKeyDown={onKeyDown}
                            // правая кнопка в меню — его собственная: ни браузерного меню, ни обработчиков владельца
                            onContextMenu={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                            data-testid={testId}
                            {...data}
                        >
                            <span id={labelId} hidden>
                                {label}
                            </span>
                            {header}
                            {/* меню у поля группы не наследует её подпись и сообщение */}
                            <FieldA11yContext value={null}>
                                <MenuContext value={menuContext}>
                                    {items ? (
                                        <List
                                            list={items}
                                            className="rf-anchored-list-dialog__list"
                                            data-testid={`${testId}-menu`}
                                        />
                                    ) : (
                                        <CustomMenu
                                            id={menuId}
                                            labelledBy={labelId}
                                            testId={`${testId}-menu`}
                                            open={opened}
                                        >
                                            {children}
                                        </CustomMenu>
                                    )}
                                </MenuContext>
                            </FieldA11yContext>
                        </Popover.Dropdown>
                    </Popover>
                </Portal>
            )}
        </>
    );
};

export default ContextMenu;
